import { useEffect, useState } from 'react';

/** How long the confirmation stays up before the button goes back to its label. */
const CONFIRM_MS = 2000;

interface CopyButtonProps {
  text: string;
  /** Verb-first label, e.g. `Copy caption`. */
  label?: string;
  variant?: 'primary' | 'secondary';
}

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Copies text to the clipboard and says so.
 *
 * The clipboard API is permissioned and absent on insecure origins, so a
 * failure is reported plainly rather than swallowed — the user can still select
 * the text themselves.
 */
export function CopyButton({ text, label = 'Copy', variant = 'secondary' }: CopyButtonProps) {
  const [state, setState] = useState<CopyState>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [state]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
  }

  const className = variant === 'primary' ? 'button button--primary' : 'button';

  return (
    <button className={className} type="button" onClick={() => void handleCopy()}>
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy blocked' : label}
    </button>
  );
}
